import firebase from "firebase";
import {
  createContext,
  FC,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import ListWrapper from "../components/ListWrapper";
import { firestore } from "../lib/firebase";
import { useAuth } from "./AuthProvider";
import { colors } from "../styles/theme";

const VideosContainer = styled(ListWrapper)`
  position: absolute;
  top: 6rem;
  right: 3.5rem;
  z-index: 2;
  pointer-events: none;
`;

const Video = styled.video<{ collapsed: boolean }>`
  display: ${({ collapsed }) => (collapsed ? "none" : "block")};
  width: 16rem;
  height: 10rem;
  object-fit: cover;
  border-radius: 4px;
`;

const CollapseButton = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: ${colors.background200};
  cursor: pointer;
  pointer-events: auto;

  > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

interface ContextProps {
  pc: MutableRefObject<RTCPeerConnection>;
  localStream: MutableRefObject<MediaStream>;
  remoteStream: MutableRefObject<MediaStream>;
  callId: string;
  audioActive: boolean;
  videoActive: boolean;
  initStreams: Function;
  makeOffer: Function;
  makeAnswer: Function;
  hangUp: Function;
  setAudioActive: Function;
  setVideoActive: Function;
}

interface CallDataProps {
  offer: Object | undefined;
  answer: Object | undefined;
  participants: Array<string>;
}

const WebRTCContext = createContext({} as ContextProps);

export const useWebRTC = () => useContext(WebRTCContext);

const WebRTCProvider: FC = ({ children }) => {
  const servers = {
    iceServers: [
      {
        urls: [
          "stun:stun1.l.google.com:19302",
          "stun:stun2.l.google.com:19302",
        ],
      },
    ],
    iceCandidatePoolSize: 10,
  };
  const pc = useRef(new RTCPeerConnection(servers));

  const [callId, setCallId] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [audioActive, setAudioActive] = useState(true);
  const [videoActive, setVideoActive] = useState(true);
  const localStream: MutableRefObject<MediaStream> = useRef(new MediaStream());
  const remoteStream: MutableRefObject<MediaStream> = useRef(new MediaStream());
  const localVideo: MutableRefObject<HTMLVideoElement | null> = useRef(null);
  const remoteVideo: MutableRefObject<HTMLVideoElement | null> = useRef(null);
  const { user } = useAuth();

  async function initStreams(videoOn: boolean = true) {
    // pc.current = new RTCPeerConnection(servers);

    localStream.current = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: videoOn,
    });

    // MUTES FOR REMOTE
    // localStream.current.getAudioTracks()[0].enabled = false;

    localStream.current.getTracks().forEach((track: MediaStreamTrack) => {
      pc.current.addTrack(track, localStream.current);
    });

    pc.current.ontrack = (event) => {
      event.streams[0].getTracks().forEach((track) => {
        remoteStream.current.addTrack(track);
      });
    };

    // createVideo(localStream.current, true);
    // createVideo(remoteStream.current, false);
  }

  async function makeOffer(cid: string) {
    const callDoc = firestore.collection("calls").doc(cid);
    const offerCandidates = callDoc.collection("offerCandidates");
    const answerCandidates = callDoc.collection("answerCandidates");

    pc.current.onicecandidate = (event) => {
      event.candidate && offerCandidates.add(event.candidate.toJSON());
    };

    const offerDescription = await pc.current.createOffer();
    await pc.current.setLocalDescription(offerDescription);

    const offer = {
      sdp: offerDescription.sdp,
      type: offerDescription.type,
    };

    await callDoc.set({
      offer,
      participants: [user.auth.uid],
    });

    callDoc.onSnapshot((snapshot) => {
      if (!snapshot.exists) {
        // Hang up too
        clearAfterCall();
      }
    });

    callDoc.onSnapshot((snapshot) => {
      const data = snapshot.data();
      if (!pc.current.currentRemoteDescription && data?.answer) {
        const answerDescription = new RTCSessionDescription(data.answer);
        pc.current.setRemoteDescription(answerDescription);
      }
    });

    answerCandidates.onSnapshot((snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          try {
            const candidate = new RTCIceCandidate(change.doc.data());
            pc.current.addIceCandidate(candidate);
          } catch (e) {
            console.error(e);
          }
        }
      });
    });

    setCallId(cid);
  }

  async function makeAnswer(cid: string) {
    const callDoc = firestore.collection("calls").doc(cid);
    const offerCandidates = callDoc.collection("offerCandidates");
    const answerCandidates = callDoc.collection("answerCandidates");

    callDoc.onSnapshot((snapshot) => {
      if (!snapshot.exists) {
        // Hang up too
        clearAfterCall();
      }
    });

    pc.current.onicecandidate = (event) => {
      event.candidate && answerCandidates.add(event.candidate.toJSON());
    };

    const callData = (await callDoc.get()).data();

    const offerDescription = callData?.offer;
    await pc.current.setRemoteDescription(
      new RTCSessionDescription(offerDescription)
    );

    const answerDescription = await pc.current.createAnswer();
    await pc.current.setLocalDescription(answerDescription);

    const answer = {
      sdp: answerDescription.sdp,
      type: answerDescription.type,
    };

    await callDoc.update({
      answer,
      participants: firebase.firestore.FieldValue.arrayUnion(user.auth.uid),
    });

    offerCandidates.onSnapshot((snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          try {
            const candidate = new RTCIceCandidate(change.doc.data());
            pc.current.addIceCandidate(candidate);
          } catch (e) {
            console.error(e);
          }
        }
      });
    });

    setCallId(cid);
  }

  const hangUp = async () => {
    if (!callId) return;

    // localStream.current.getTracks().forEach((track) => track.stop());
    // remoteStream.current.getTracks().forEach((track) => track.stop());
    // localStream.current = new MediaStream();
    // remoteStream.current = new MediaStream();

    const callDoc = firestore.collection("calls").doc(callId);

    const offerCandidates = await callDoc.collection("offerCandidates").get();
    for (const doc of offerCandidates.docs) {
      await doc.ref.delete();
    }

    const answerCandidates = await callDoc.collection("answerCandidates").get();
    for (const doc of answerCandidates.docs) {
      await doc.ref.delete();
    }

    await callDoc.delete();

    // #region to remove
    // const callData = (await callDoc.get()).data() as CallDataProps;

    // const participant = callData.participants.find((p) => p === user.auth.uid);
    // console.log(participant);

    // if (participant) {
    //   // callDoc.onSnapshot(async (snapshot) => {
    //   //   const data = snapshot.data();
    //   //   if (data?.participants.length <= 0) {
    //   //     const offerCandidates = await snapshot.ref
    //   //       .collection("offerCandidates")
    //   //       .get();
    //   //     for (const doc of offerCandidates.docs) {
    //   //       await doc.ref.delete();
    //   //     }

    //   //     const answerCandidates = await snapshot.ref
    //   //       .collection("answerCandidates")
    //   //       .get();
    //   //     for (const doc of answerCandidates.docs) {
    //   //       await doc.ref.delete();
    //   //     }

    //   //     await snapshot.ref.delete();
    //   //   }
    //   // });

    //   await callDoc.update({
    //     participants: firebase.firestore.FieldValue.arrayRemove(participant),
    //   });

    //   if (callData.participants.length <= 1) {
    //     const offerCandidates = await firestore
    //       .collection("calls")
    //       .doc(callId)
    //       .collection("offerCandidates")
    //       .get();
    //     for (const doc of offerCandidates.docs) {
    //       doc.ref.delete();
    //     }

    //     const answerCandidates = await firestore
    //       .collection("calls")
    //       .doc(callId)
    //       .collection("answerCandidates")
    //       .get();
    //     for (const doc of answerCandidates.docs) {
    //       doc.ref.delete();
    //     }

    //     await firestore.collection("calls").doc(callId).delete();
    //   }
    // }
    // #endregion

    // pc.current.close();
    // pc.current = new RTCPeerConnection(servers);

    // setCallId("");
  };

  function clearAfterCall() {
    localStream.current.getTracks().forEach((track) => track.stop());
    remoteStream.current.getTracks().forEach((track) => track.stop());
    localStream.current = new MediaStream();
    remoteStream.current = new MediaStream();
    pc.current.close();
    pc.current = new RTCPeerConnection(servers);
    setCallId("");
    setCollapsed(false);
  }

  useEffect(() => {
    if (!callId) return;

    if (localVideo.current && remoteVideo.current) {
      localVideo.current.srcObject = localStream.current;
      remoteVideo.current.srcObject = remoteStream.current;
    }
  }, [callId]);

  useEffect(() => {
    const tracks = localStream.current.getAudioTracks();
    if (tracks.length) tracks[0].enabled = audioActive;
  }, [audioActive]);

  useEffect(() => {
    const tracks = localStream.current.getVideoTracks();
    if (tracks.length) tracks[0].enabled = videoActive;
  }, [videoActive]);

  const value = {
    pc,
    localStream,
    remoteStream,
    callId,
    audioActive,
    videoActive,
    initStreams,
    makeOffer,
    makeAnswer,
    hangUp,
    setAudioActive,
    setVideoActive,
  };

  return (
    <WebRTCContext.Provider value={value}>
      {children}
      {callId && (
        <VideosContainer>
          <CollapseButton onClick={() => setCollapsed(!collapsed)}>
            {!collapsed ? (
              <FaChevronDown size={24} />
            ) : (
              <FaChevronUp size={24} />
            )}
          </CollapseButton>
          <Video
            ref={localVideo}
            autoPlay={true}
            collapsed={collapsed}
            muted={true}
          >
            No video
          </Video>
          <Video ref={remoteVideo} autoPlay={true} collapsed={collapsed}>
            No video
          </Video>
        </VideosContainer>
      )}
    </WebRTCContext.Provider>
  );
};

export default WebRTCProvider;
